import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { store } from "../../store";
import { v4 as uuidv4 } from 'uuid';

export type NotificationItem = {
    message: string,
    type: "success" | "failure" | "info"
}

export type NotificationItemWithId = {
    id: string,
    message: string,
    type: "success" | "failure" | "info",
    isOpen: boolean
}

export type NotificationState = {
    notifications: NotificationItemWithId[]
}

const initialState: NotificationState = {
    notifications: []
};

export const notificationSlice = createSlice({
    name: 'notification',
    initialState: initialState,
    reducers: {
        addNotification: (state, action: PayloadAction<NotificationItemWithId>) => {
            state.notifications.push(action.payload);
        },
        closeNotification: (state, action: PayloadAction<string>) => {
            const notificationIndex = state.notifications.findIndex(x => x.id === action.payload);

            if (notificationIndex > -1) {
                const notification = state.notifications[notificationIndex];
                notification.isOpen = false;
            }
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter(x => x.id !== action.payload);
        },
    }
});

export function addNotification(notification: NotificationItem): string {
    const id = uuidv4();

    store.dispatch(notificationSlice.actions.addNotification({
        ...notification,
        id: id,
        isOpen: true
    }));

    return id;
}

export function addTimedNotification(notification: NotificationItem, time?: number) {
    const notificationId = addNotification(notification);

    setTimeout(() => {
        closeNotification(notificationId);
    }, time ?? 3000);

    return notificationId;
}

export function showFailure(message: string, seconds?: number) {
    const secondsOrDefault = seconds ? seconds : 6;

    addTimedNotification({
        message: message,
        type: "failure"
    }, secondsOrDefault * 1000);
}

export function showSuccess(message: string, milliseconds?: number) {
    addTimedNotification({
        message: message,
        type: "success"
    }, milliseconds ?? 3000);
}

export function closeNotification(id: string) {
    store.dispatch(notificationSlice.actions.closeNotification(id));
}

export function removeNotification(id: string) {
    store.dispatch(notificationSlice.actions.removeNotification(id));
}

export default notificationSlice.reducer;